/**
 * useCopyToClipboard
 *
 * Shared copy-to-clipboard hook, extracted from the pattern duplicated across
 * ListingDetailPage.tsx's CopyPublicLinkButton (lines 201-263) and
 * IntakeAddressBanner (lines 274-368), plus lead-detail/ApplicantsTab.tsx's
 * "Copy Regen Link" affordance (lines 463-472). All three call
 * `navigator.clipboard.writeText` inside a try/catch, flip `copied` to `true`
 * for 2000ms via `setTimeout`, and treat a thrown error as a copy failure.
 *
 * This hook covers the union of those call sites' needs without changing any
 * of their current behavior:
 *   - `copied` / `copy()` alone is enough for call sites that only show a
 *     "Copied!" state (IntakeAddressBanner, ApplicantsTab's regen-link copy).
 *   - `copyFailed` is additionally consumed by call sites that reveal a
 *     manual-copy fallback input on clipboard failure (CopyPublicLinkButton).
 *     The hook only tracks the boolean; selecting a fallback input ref stays
 *     the caller's responsibility (e.g. via a `useEffect` on `copyFailed`),
 *     since the hook has no opinion on what, if anything, a caller renders.
 */

import { useCallback, useState } from 'react';

export interface UseCopyToClipboardResult {
  copied: boolean;
  copyFailed: boolean;
  copy: (text: string) => Promise<void>;
}

export function useCopyToClipboard(): UseCopyToClipboardResult {
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  const copy = useCallback(async (text: string) => {
    setCopyFailed(false);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access denied/unavailable — let the caller decide how to
      // recover (e.g. reveal a fallback input for manual copy).
      setCopyFailed(true);
    }
  }, []);

  return { copied, copyFailed, copy };
}
